/**
 * Knowledge Base Tab Controller
 * Handles display and interaction with KB documents
 */

import { CanisterClient } from '../canister-client';
import { KBAgent } from './kb-agent';
import type { KBDocument, DocumentType } from '../types/candid-types';

export class KnowledgeTab {
    private canisterClient: CanisterClient;
    private kbAgent: KBAgent | null = null;
    private allDocuments: KBDocument[] = [];
    private currentFilter: 'all' | 'errors' | 'resolutions' | 'best_practices' | 'adrs' = 'all';

    constructor(canisterClient: CanisterClient) {
        this.canisterClient = canisterClient;

        // Initialize KB agent for direct canister communication
        const canisterId = (canisterClient as any).canisterIds?.knowledge_base;
        if (canisterId) {
            this.kbAgent = new KBAgent(canisterId);
            console.log('✅ KBAgent initialized for direct communication');
        } else {
            console.warn('⚠️ knowledge_base canister ID not found, falling back to proxy');
        }
    }

    /**
     * Initialize the Knowledge Base tab
     */
    async init(): Promise<void> {
        console.log('📚 Initializing Knowledge Base tab...');

        try {
            // Ensure the knowledge-content div is visible
            const knowledgeContent = document.getElementById('knowledge-content');
            if (knowledgeContent && knowledgeContent.classList.contains('hidden')) {
                knowledgeContent.classList.remove('hidden');
            }

            // Load statistics and documents
            await Promise.all([
                this.loadStatistics(),
                this.loadDocuments()
            ]);

            // Set up search functionality and filters
            this.setupSearch();
            this.setupFilterHandlers();

            console.log('✅ Knowledge Base initialized');
        } catch (error) {
            console.error('❌ Failed to initialize Knowledge Base:', error);
            this.showError('Failed to load Knowledge Base. Please try again.');
        }
    }

    /**
     * Load and display KB statistics
     */
    private async loadStatistics(): Promise<void> {
        try {
            // Use direct KB agent if available, otherwise fall back to proxy
            const stats = this.kbAgent
                ? await this.kbAgent.getStatistics()
                : await this.canisterClient.getKBStatistics();

            // Update statistics cards
            this.updateElement('kb-total-count', Number(stats.total_documents).toString());
            this.updateElement('kb-errors-count', Number(stats.errors).toString());
            this.updateElement('kb-resolutions-count', Number(stats.resolutions).toString());
            this.updateElement('kb-best-practices-count', Number(stats.best_practices).toString());
            this.updateElement('kb-adrs-count', Number(stats.adrs).toString());
        } catch (error) {
            console.error('Failed to load KB statistics:', error);
        }
    }

    /**
     * Load all documents from KB
     */
    private async loadDocuments(): Promise<void> {
        try {
            // Use direct KB agent if available, otherwise fall back to proxy
            const docs = this.kbAgent
                ? await this.kbAgent.listDocuments()
                : await this.canisterClient.listKBDocuments();

            this.allDocuments = docs;

            // Render document list
            this.renderDocumentList(docs);
        } catch (error) {
            console.error('Failed to load KB documents:', error);
            throw error;
        }
    }

    /**
     * Render document list grouped by type
     */
    private renderDocumentList(documents: KBDocument[]): void {
        const mainContent = document.getElementById('kb-main-content');
        if (!mainContent) {
            console.error('❌ kb-main-content element not found!');
            return;
        }

        // Remove hidden class if present
        if (mainContent.classList.contains('hidden')) {
            mainContent.classList.remove('hidden');
        }

        // Group documents by type
        const grouped = this.groupDocumentsByType(documents);

        let html = '<div class="space-y-8">';

        // Render each group
        if (grouped.errors.length > 0) {
            html += this.renderDocumentGroup('Errors', 'alert-circle', 'red', grouped.errors);
        }
        if (grouped.resolutions.length > 0) {
            html += this.renderDocumentGroup('Resolutions', 'check-circle', 'green', grouped.resolutions);
        }
        if (grouped.bestPractices.length > 0) {
            html += this.renderDocumentGroup('Best Practices', 'book', 'purple', grouped.bestPractices);
        }
        if (grouped.adrs.length > 0) {
            html += this.renderDocumentGroup('ADRs', 'git-branch', 'yellow', grouped.adrs);
        }

        if (documents.length === 0) {
            html += `
                <div class="text-center text-gray-600 py-12">
                    <i data-feather="book-open" class="mx-auto mb-4" style="width: 48px; height: 48px;"></i>
                    <p class="text-lg mb-2 font-semibold">Documents Available But Not Yet Displayable</p>
                    <p class="text-sm mb-4">The Knowledge Base contains documents (see statistics above),<br>
                    but the UI display is still being implemented.</p>
                    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto text-left">
                        <p class="text-sm text-blue-900 mb-2"><strong>Current Workaround:</strong></p>
                        <p class="text-sm text-blue-800 mb-2">You can view KB documents using the CLI:</p>
                        <code class="block bg-blue-100 px-3 py-2 rounded text-xs text-blue-900 font-mono">
                            dfx canister call knowledge_base list_documents '(null)'<br>
                            dfx canister call knowledge_base get_document '("BP-006")'
                        </code>
                        <p class="text-xs text-blue-700 mt-2">The proxy parser needs enhancement to handle complex KB record structures.</p>
                    </div>
                </div>
            `;
        }

        html += '</div>';
        mainContent.innerHTML = html;

        // Re-initialize feather icons (with error handling)
        try {
            if (window.feather) {
                window.feather.replace();
            }
        } catch (error) {
            console.warn('⚠️ Feather icons failed to replace:', error);
        }
    }

    /**
     * Group documents by document type
     */
    private groupDocumentsByType(documents: KBDocument[]): {
        errors: KBDocument[];
        resolutions: KBDocument[];
        bestPractices: KBDocument[];
        adrs: KBDocument[];
    } {
        const grouped = {
            errors: [] as KBDocument[],
            resolutions: [] as KBDocument[],
            bestPractices: [] as KBDocument[],
            adrs: [] as KBDocument[]
        };

        for (const doc of documents) {
            if ('Error' in doc.doc_type) {
                grouped.errors.push(doc);
            } else if ('Resolution' in doc.doc_type) {
                grouped.resolutions.push(doc);
            } else if ('BestPractice' in doc.doc_type) {
                grouped.bestPractices.push(doc);
            } else if ('ADR' in doc.doc_type) {
                grouped.adrs.push(doc);
            }
        }

        // Sort each group by ID
        grouped.errors.sort((a, b) => a.id.localeCompare(b.id));
        grouped.resolutions.sort((a, b) => a.id.localeCompare(b.id));
        grouped.bestPractices.sort((a, b) => a.id.localeCompare(b.id));
        grouped.adrs.sort((a, b) => a.id.localeCompare(b.id));

        return grouped;
    }

    /**
     * Get icon color class based on color name
     */
    private getIconColorClass(color: string): string {
        const colorMap: { [key: string]: string } = {
            'red': 'text-red-600',
            'green': 'text-green-600',
            'purple': 'text-purple-600',
            'yellow': 'text-yellow-600',
            'blue': 'text-blue-600'
        };
        return colorMap[color] || 'text-gray-600';
    }

    /**
     * Render a group of documents
     */
    private renderDocumentGroup(title: string, icon: string, color: string, documents: KBDocument[]): string {
        const iconColorClass = this.getIconColorClass(color);

        let html = `
            <div class="space-y-4">
                <div class="flex items-center gap-2">
                    <i data-feather="${icon}" class="${iconColorClass}"></i>
                    <h2 class="text-xl font-semibold">${title}</h2>
                    <span class="text-sm text-gray-500">(${documents.length})</span>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        `;

        for (const doc of documents) {
            html += this.renderDocumentCard(doc, color);
        }

        html += `
                </div>
            </div>
        `;

        return html;
    }

    /**
     * Get color classes for badge based on color name
     */
    private getBadgeClasses(color: string): string {
        const colorMap: { [key: string]: string } = {
            'red': 'bg-red-100 text-red-700',
            'green': 'bg-green-100 text-green-700',
            'purple': 'bg-purple-100 text-purple-700',
            'yellow': 'bg-yellow-100 text-yellow-700',
            'blue': 'bg-blue-100 text-blue-700'
        };
        return colorMap[color] || 'bg-gray-100 text-gray-700';
    }

    /**
     * Render a single document card
     */
    private renderDocumentCard(doc: KBDocument, color: string): string {
        const severity = Array.isArray(doc.severity) && doc.severity.length > 0 ? doc.severity[0] : null;
        const badgeClasses = this.getBadgeClasses(color);
        const severityBadge = severity
            ? `<span class="px-2 py-1 text-xs rounded-full ${badgeClasses}">${severity}</span>`
            : '';

        return `
            <div class="device-card cursor-pointer" onclick="knowledgeTab.viewDocument('${doc.id}')">
                <div class="flex items-start justify-between mb-2">
                    <h3 class="font-semibold text-lg">${this.escapeHtml(doc.id)}</h3>
                    ${severityBadge}
                </div>
                <h4 class="font-medium mb-2">${this.escapeHtml(doc.title)}</h4>
                <p class="text-sm text-gray-600 mb-3 line-clamp-2">${this.escapeHtml(doc.summary)}</p>
                <div class="flex items-center justify-between text-xs text-gray-500">
                    <span class="flex items-center gap-1">
                        <i data-feather="tag" style="width: 14px; height: 14px;"></i>
                        ${doc.keywords.slice(0, 3).join(', ')}
                    </span>
                    <span>v${doc.version}</span>
                </div>
            </div>
        `;
    }

    /**
     * View a specific document in detail
     */
    async viewDocument(docId: string): Promise<void> {
        console.log(`📖 Viewing document: ${docId}`);

        try {
            // Use direct KB agent if available, otherwise fall back to proxy
            const result = this.kbAgent
                ? await this.kbAgent.getDocument(docId)
                : await this.canisterClient.getKBDocument(docId);

            this.renderDocumentDetail(result);
        } catch (error) {
            console.error('Failed to load document:', error);
            this.showError(`Failed to load document ${docId}`);
        }
    }

    /**
     * Render document detail view
     */
    private renderDocumentDetail(doc: KBDocument): void {
        const mainContent = document.getElementById('kb-main-content');
        if (!mainContent) return;

        const severity = Array.isArray(doc.severity) && doc.severity.length > 0 ? doc.severity[0] : null;
        const docTypeLabel = this.getDocTypeLabel(doc.doc_type);

        let html = `
            <div class="space-y-6">
                <!-- Header -->
                <div class="flex items-start justify-between">
                    <div class="flex-1">
                        <div class="flex items-center gap-3 mb-2">
                            <button onclick="knowledgeTab.backToList()" class="btn btn-secondary btn-sm">
                                <i data-feather="arrow-left"></i>
                                Back
                            </button>
                            <span class="px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-700">${docTypeLabel}</span>
                            ${severity ? `<span class="px-3 py-1 text-sm rounded-full bg-red-100 text-red-700">${severity}</span>` : ''}
                        </div>
                        <h1 class="text-2xl font-bold mb-1">${this.escapeHtml(doc.id)}: ${this.escapeHtml(doc.title)}</h1>
                        <p class="text-gray-600">${this.escapeHtml(doc.summary)}</p>
                    </div>
                </div>

                <!-- Metadata -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                        <p class="text-sm text-gray-600 mb-1">Component</p>
                        <p class="font-medium">${this.escapeHtml(doc.component)}</p>
                    </div>
                    <div>
                        <p class="text-sm text-gray-600 mb-1">Version</p>
                        <p class="font-medium">v${doc.version}</p>
                    </div>
                    <div>
                        <p class="text-sm text-gray-600 mb-1">Keywords</p>
                        <p class="font-medium">${doc.keywords.slice(0, 5).join(', ')}</p>
                    </div>
                </div>

                <!-- Content -->
                <div class="prose max-w-none">
                    ${this.renderMarkdown(doc.content)}
                </div>

                <!-- Related Documents -->
                ${doc.related_docs.length > 0 ? `
                    <div class="border-t pt-6">
                        <h3 class="text-lg font-semibold mb-3">Related Documents</h3>
                        <div class="flex flex-wrap gap-2">
                            ${doc.related_docs.map(relId => `
                                <button onclick="knowledgeTab.viewDocument('${relId}')" class="btn btn-secondary btn-sm">
                                    ${this.escapeHtml(relId)}
                                </button>
                            `).join('')}
                        </div>
                    </div>
                ` : ''}
            </div>
        `;

        mainContent.innerHTML = html;

        // Re-initialize feather icons (with error handling)
        try {
            if (window.feather) {
                console.log('🎨 Replacing feather icons...');
                window.feather.replace();
                console.log('✅ Feather icons replaced successfully');
            }
        } catch (error) {
            console.warn('⚠️ Feather icons failed to replace (non-critical):', error);
            // Continue anyway - icons are nice but not critical
        }

        console.log('🎉 Rendering completed successfully');
    }

    /**
     * Basic markdown rendering
     * Note: For production, use a library like marked.js
     */
    private renderMarkdown(content: string): string {
        let html = this.escapeHtml(content);

        // Headers
        html = html.replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold mt-6 mb-3">$1</h3>');
        html = html.replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold mt-8 mb-4">$1</h2>');
        html = html.replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold mt-8 mb-4">$1</h1>');

        // Bold and italic
        html = html.replace(/\*\*\*([^*]+)\*\*\*/g, '<strong><em>$1</em></strong>');
        html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
        html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');

        // Code blocks
        html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre class="bg-gray-100 p-4 rounded-lg overflow-x-auto my-4"><code>$2</code></pre>');

        // Inline code
        html = html.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-2 py-1 rounded text-sm">$1</code>');

        // Lists
        html = html.replace(/^\- (.*$)/gim, '<li class="ml-4">$1</li>');
        html = html.replace(/(<li.*<\/li>)/s, '<ul class="list-disc ml-6 my-2">$1</ul>');

        // Links
        html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:underline">$1</a>');

        // Line breaks
        html = html.replace(/\n\n/g, '</p><p class="mb-4">');
        html = '<p class="mb-4">' + html + '</p>';

        return html;
    }

    /**
     * Get human-readable label for document type
     */
    private getDocTypeLabel(docType: DocumentType): string {
        if ('Error' in docType) return 'Error';
        if ('Resolution' in docType) return 'Resolution';
        if ('BestPractice' in docType) return 'Best Practice';
        if ('ADR' in docType) return 'Architecture Decision Record';
        return 'Unknown';
    }

    /**
     * Return to document list view
     */
    backToList(): void {
        // Maintain current filter when returning to list
        const filtered = this.applyFilter(this.allDocuments);
        this.renderDocumentList(filtered);
    }

    /**
     * Set up filter click handlers on statistics cards
     */
    private setupFilterHandlers(): void {
        // Add click handlers to statistics cards
        const totalCard = document.getElementById('kb-total-count')?.closest('.stat-card');
        const errorsCard = document.getElementById('kb-errors-count')?.closest('.stat-card');
        const resolutionsCard = document.getElementById('kb-resolutions-count')?.closest('.stat-card');
        const bestPracticesCard = document.getElementById('kb-best-practices-count')?.closest('.stat-card');
        const adrsCard = document.getElementById('kb-adrs-count')?.closest('.stat-card');

        if (totalCard) {
            totalCard.classList.add('cursor-pointer', 'hover:shadow-lg', 'transition-shadow');
            totalCard.addEventListener('click', () => this.filterDocuments('all'));
        }

        if (errorsCard) {
            errorsCard.classList.add('cursor-pointer', 'hover:shadow-lg', 'transition-shadow');
            errorsCard.addEventListener('click', () => this.filterDocuments('errors'));
        }

        if (resolutionsCard) {
            resolutionsCard.classList.add('cursor-pointer', 'hover:shadow-lg', 'transition-shadow');
            resolutionsCard.addEventListener('click', () => this.filterDocuments('resolutions'));
        }

        if (bestPracticesCard) {
            bestPracticesCard.classList.add('cursor-pointer', 'hover:shadow-lg', 'transition-shadow');
            bestPracticesCard.addEventListener('click', () => this.filterDocuments('best_practices'));
        }

        if (adrsCard) {
            adrsCard.classList.add('cursor-pointer', 'hover:shadow-lg', 'transition-shadow');
            adrsCard.addEventListener('click', () => this.filterDocuments('adrs'));
        }
    }

    /**
     * Filter documents by type
     */
    filterDocuments(filter: 'all' | 'errors' | 'resolutions' | 'best_practices' | 'adrs'): void {
        this.currentFilter = filter;

        // Update active state on cards
        this.updateFilterActiveState();

        // Apply filter
        const filtered = this.applyFilter(this.allDocuments);
        this.renderDocumentList(filtered);
    }

    /**
     * Apply current filter to document list
     */
    private applyFilter(documents: KBDocument[]): KBDocument[] {
        if (this.currentFilter === 'all') {
            return documents;
        }

        return documents.filter(doc => {
            switch (this.currentFilter) {
                case 'errors':
                    return 'Error' in doc.doc_type;
                case 'resolutions':
                    return 'Resolution' in doc.doc_type;
                case 'best_practices':
                    return 'BestPractice' in doc.doc_type;
                case 'adrs':
                    return 'ADR' in doc.doc_type;
                default:
                    return true;
            }
        });
    }

    /**
     * Update visual state of filter cards
     */
    private updateFilterActiveState(): void {
        // Remove active class from all cards
        const allCards = document.querySelectorAll('.stat-card');
        allCards.forEach(card => {
            card.classList.remove('ring-2', 'ring-blue-500', 'bg-blue-50');
        });

        // Add active class to selected card
        let activeCardId: string | null = null;
        switch (this.currentFilter) {
            case 'all':
                activeCardId = 'kb-total-count';
                break;
            case 'errors':
                activeCardId = 'kb-errors-count';
                break;
            case 'resolutions':
                activeCardId = 'kb-resolutions-count';
                break;
            case 'best_practices':
                activeCardId = 'kb-best-practices-count';
                break;
            case 'adrs':
                activeCardId = 'kb-adrs-count';
                break;
        }

        if (activeCardId) {
            const activeCard = document.getElementById(activeCardId)?.closest('.stat-card');
            if (activeCard) {
                activeCard.classList.add('ring-2', 'ring-blue-500', 'bg-blue-50');
            }
        }
    }

    /**
     * Set up search functionality
     */
    private setupSearch(): void {
        const searchInput = document.getElementById('kb-search-input') as HTMLInputElement;
        if (!searchInput) return;

        let searchTimeout: number;
        searchInput.addEventListener('input', (e) => {
            const query = (e.target as HTMLInputElement).value.trim();

            // Debounce search
            clearTimeout(searchTimeout);
            searchTimeout = window.setTimeout(() => {
                this.performSearch(query);
            }, 300);
        });
    }

    /**
     * Perform search on documents
     */
    private performSearch(query: string): void {
        // Start with filtered documents based on current filter
        let documents = this.applyFilter(this.allDocuments);

        if (!query) {
            // Show filtered documents if search is empty
            this.renderDocumentList(documents);
            return;
        }

        const lowerQuery = query.toLowerCase();
        const filtered = documents.filter(doc => {
            return (
                doc.id.toLowerCase().includes(lowerQuery) ||
                doc.title.toLowerCase().includes(lowerQuery) ||
                doc.summary.toLowerCase().includes(lowerQuery) ||
                doc.keywords.some(kw => kw.toLowerCase().includes(lowerQuery)) ||
                doc.content.toLowerCase().includes(lowerQuery)
            );
        });

        this.renderDocumentList(filtered);
    }

    /**
     * Show error message
     */
    private showError(message: string): void {
        const mainContent = document.getElementById('kb-main-content');
        if (!mainContent) return;

        mainContent.innerHTML = `
            <div class="text-center text-red-600 py-12">
                <i data-feather="alert-triangle" class="mx-auto mb-4" style="width: 48px; height: 48px;"></i>
                <p class="text-lg mb-2">${this.escapeHtml(message)}</p>
            </div>
        `;

        try {
            if (window.feather) {
                window.feather.replace();
            }
        } catch (error) {
            console.warn('⚠️ Feather icons failed in error display:', error);
        }
    }

    /**
     * Update element text content
     */
    private updateElement(id: string, value: string): void {
        const element = document.getElementById(id);
        if (element) {
            element.textContent = value;
        }
    }

    /**
     * Escape HTML to prevent XSS
     */
    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

// Declare global instance for onclick handlers
declare global {
    interface Window {
        knowledgeTab: KnowledgeTab;
    }
}
